import {MutableRefObject, useCallback, useEffect, useRef, useState} from "react";
import type {TFunction} from "i18next";
import VolumeMeter from "../../helper/VolumeMeter";
import type {AppNotificationCreateAttributes} from "../NotificationContext";
import {deviceLabel} from "./deviceLabels";
import {MIC_TEST_PLAYBACK_CAP_MS, MIC_TEST_RECORD_MS, SPEAKER_TEST_TTL_MS} from "./timings";

export type MicTestState = 'idle' | 'recording' | 'playing'

// Derived here, not at the two panels, so they cannot drift (RISKS.md/P2.28). Unlike the speaker's,
// this verb changes: clicking does not restart — mid-take it finishes, mid-playback it stops.
export const micTestLabelKey = (state: MicTestState) =>
  state === 'idle' ? 'lobby.microphoneTest' : 'lobby.microphoneTestStop'

export const micTestStatusKey = (state: MicTestState) =>
  state === 'recording' ? 'lobby.microphoneTestRecording'
    : state === 'playing' ? 'lobby.microphoneTestPlaying'
      : null

const useMicrophoneTest = ({
  stream, volumeMeter, applySinkIdRef, speakersRef, selectedSpeakerRef, checkedIn,
  audioEnabledRef, micOsMutedRef, micUnavailable, retractSpeakerTest, createNotification, hideNotification, t,
}: {
  stream: MediaStream
  volumeMeter: VolumeMeter
  applySinkIdRef: MutableRefObject<(el: HTMLMediaElement | null) => Promise<void>>
  speakersRef: MutableRefObject<MediaDeviceInfo[]>
  selectedSpeakerRef: MutableRefObject<string | null>
  checkedIn: boolean
  audioEnabledRef: MutableRefObject<boolean>
  micOsMutedRef: MutableRefObject<boolean>
  micUnavailable: boolean
  retractSpeakerTest: () => void
  createNotification: (attributes: AppNotificationCreateAttributes) => string
  hideNotification: (id: string) => void
  t: TFunction
}) => {
  const [micTestState, setMicTestState] = useState<MicTestState>('idle')

  const micTestElRef = useRef<HTMLAudioElement | null>(null)
  const micTestRecorderRef = useRef<MediaRecorder | null>(null)
  // The clone the take is recorded from, and the live track muted while it runs.
  const micTestTakeRef = useRef<MediaStreamTrack | null>(null)
  const micTestPublishRef = useRef<MediaStreamTrack | null>(null)
  const micTestUrlRef = useRef<string | null>(null)
  const micTestGenerationRef = useRef(0)
  // Never mirror state into this during render: a re-render between an eager write and its state
  // commit would revert the guard, and the provider re-renders ~20x/s (RISKS.md/P2.19).
  const micTestStateRef = useRef<MicTestState>('idle')
  // Called through the ref so a listener from an earlier render still cancels the live run.
  const stopMicTestRef = useRef<(retract?: boolean) => void>(() => null)

  // Leaving the green room ends the question the test toast was asking. It would otherwise survive
  // the navigation — unmounting a Notification clears its expiry timer without removing it from
  // state, so it re-mounts in the meeting room with a fresh clock.
  useEffect(() => {
    retractSpeakerTest()
    stopMicTestRef.current()
    // Not `retractSpeakerTest`: new every render, so it would cancel every microphone test at once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedIn])

  // Bumping the generation is what actually cancels: every continuation below re-checks it, so a
  // recorder's own `stop` event cannot go on to play a clip this has already thrown away.
  const stopMicTest = (retract = true) => {
    micTestGenerationRef.current++
    const recorder = micTestRecorderRef.current
    micTestRecorderRef.current = null
    if (recorder && recorder.state !== 'inactive') recorder.stop()
    micTestElRef.current?.pause()

    // Un-mute the publish and drop the clone. Both must happen on EVERY stop path — a publish left
    // muted is the worst thing this feature could leak, and it is silent by construction.
    const published = micTestPublishRef.current
    micTestPublishRef.current = null
    if (published) published.enabled = true
    const take = micTestTakeRef.current
    micTestTakeRef.current = null
    if (take) {
      take.stop()
      // `stream` is stable and its tracks are swapped in place, so this is the right restore even if
      // the microphone was re-captured mid-test.
      volumeMeter.setStream(stream)
    }

    // The clip exists only as this URL, so revoking it is the whole of "nothing was kept".
    if (micTestUrlRef.current) {
      URL.revokeObjectURL(micTestUrlRef.current)
      micTestUrlRef.current = null
    }
    // Abandoning the test must retract its toast: it describes a run that is no longer happening,
    // and crossing into the meeting room it re-mounts with a fresh clock and green-room copy. The
    // natural end passes `false` — there the toast is the result, and its ttl is the reading time.
    if (retract) {
      hideNotification('mic-test')
      hideNotification('mic-test-failed')
    }
    // Written here, not left to the next render: two clicks inside one frame would otherwise both
    // read 'idle' and start a second recorder that nothing can reach to stop.
    micTestStateRef.current = 'idle'
    setMicTestState('idle')
  }
  stopMicTestRef.current = stopMicTest

  // Through the ref, so the identity is stable and a panel effect can depend on it.
  const cancelMicTest = useCallback(() => stopMicTestRef.current(), [])

  // Ends the take EARLY and plays it back. Deliberately not `stopMicTest`, which cancels: the
  // generation has to survive so the recorder's own `stop` event runs its playback path.
  const finishMicRecording = () => {
    const recorder = micTestRecorderRef.current
    if (recorder && recorder.state !== 'inactive') recorder.stop()
  }

  const testMicrophone = async () => {
    if (micTestStateRef.current === 'recording') {
      finishMicRecording()
      return
    }
    if (micTestStateRef.current === 'playing') {
      stopMicTest()
      return
    }

    // Audio only: `stream` carries the camera too, and MediaRecorder would happily record that.
    const tracks = stream.getAudioTracks()
    // An OS mute would run the test to completion and prove nothing: the track is live, so it
    // records silence and that reads as a broken speaker. Told, not disabled (RISKS.md/P2.41).
    const blockedKey = !audioEnabledRef.current ? 'lobby.microphoneTestMuted'
      : micOsMutedRef.current ? 'lobby.microphoneTestMutedSystem'
        : (tracks.length === 0 || typeof MediaRecorder === 'undefined') ? 'lobby.microphoneTestFailed'
          : null
    if (blockedKey) {
      hideNotification('mic-test')
      hideNotification('mic-test-failed')
      createNotification({ id: 'mic-test-failed', message: t(blockedKey), ttl: SPEAKER_TEST_TTL_MS })
      return
    }

    const generation = ++micTestGenerationRef.current
    micTestStateRef.current = 'recording'
    setMicTestState('recording')

    // Peers must hear neither the take nor the playback off the speakers. The producer holds the
    // TRACK OBJECT, so muting it would silence the recording too — hence a clone, with its own
    // `enabled`. The meter follows the clone, or the bar reads flat while the user speaks (P2.51).
    const published = tracks[0]
    const take = published.clone()
    micTestPublishRef.current = published
    micTestTakeRef.current = take
    published.enabled = false
    volumeMeter.setStream(new MediaStream([take]))

    // At the START, not at playback: the mute begins on this click, so a notice that waits for the
    // take to finish leaves the user silent and unaware. Same id — the playback toast replaces it.
    if (checkedIn) {
      hideNotification('mic-test')
      hideNotification('mic-test-failed')
      createNotification({ id: 'mic-test', message: t('lobby.microphoneTestStarted'), ttl: SPEAKER_TEST_TTL_MS })
    }

    const recorder = new MediaRecorder(new MediaStream([take]))
    micTestRecorderRef.current = recorder
    const chunks: Blob[] = []
    recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }

    recorder.onstop = async () => {
      if (generation !== micTestGenerationRef.current) return
      micTestRecorderRef.current = null

      // The stop control is live from the first frame, so a take can end before any data arrives.
      // Nothing to play and nothing wrong: go quiet rather than report a playback failure.
      const clip = new Blob(chunks, { type: recorder.mimeType })
      if (clip.size === 0) {
        stopMicTest()
        return
      }

      const el = micTestElRef.current ?? (micTestElRef.current = new Audio())
      if (micTestUrlRef.current) URL.revokeObjectURL(micTestUrlRef.current)
      micTestUrlRef.current = URL.createObjectURL(clip)
      el.src = micTestUrlRef.current
      el.volume = 1
      // The same applier the tone uses, so the playback travels the output path the call travels
      // and a routing failure raises the one taxonomy (RISKS.md/P2.39).
      await applySinkIdRef.current(el)
      if (generation !== micTestGenerationRef.current) return

      // `false` — at the natural end the toast IS the result, and its ttl is the reading time.
      el.onended = () => { if (generation === micTestGenerationRef.current) stopMicTestRef.current(false) }
      setTimeout(() => { if (generation === micTestGenerationRef.current) stopMicTestRef.current() }, MIC_TEST_PLAYBACK_CAP_MS)
      micTestStateRef.current = 'playing'
      setMicTestState('playing')

      // Time-boxed like the tone's: play() resolves on playback and rejects only on error, so it can
      // hang instead of settling.
      let rejected = false
      await Promise.race([
        el.play().catch(() => { rejected = true }),
        new Promise<void>(r => setTimeout(r, 2000)),
      ])
      if (generation !== micTestGenerationRef.current) return
      hideNotification('mic-test')
      hideNotification('mic-test-failed')
      if (rejected) {
        createNotification({ id: 'mic-test-failed', message: t('lobby.microphoneTestFailed'), ttl: SPEAKER_TEST_TTL_MS })
        stopMicTest()
      } else {
        const all = speakersRef.current
        const device = all.find(s => s.deviceId === selectedSpeakerRef.current)
        createNotification({
          id: 'mic-test',
          // Only the meeting room says peers cannot hear you — in the green room there are none,
          // and the mute would be a claim about nobody.
          message: t(checkedIn ? 'lobby.microphoneTestPlayedLive' : 'lobby.microphoneTestPlayed',
            { name: deviceLabel(device, all) || t('lobby.systemDefault') }),
          ttl: SPEAKER_TEST_TTL_MS,
        })
      }
    }

    recorder.start()
    setTimeout(() => {
      if (generation === micTestGenerationRef.current && recorder.state !== 'inactive') recorder.stop()
    }, MIC_TEST_RECORD_MS)
    // Absolute backstop for the un-mute: the playback cap is armed inside `onstop`, so it covers
    // nothing if the recorder never fires that event, and a publish left muted is the failure.
    setTimeout(() => {
      if (generation === micTestGenerationRef.current) stopMicTestRef.current()
    }, MIC_TEST_RECORD_MS + MIC_TEST_PLAYBACK_CAP_MS)
  }

  useEffect(() => () => stopMicTestRef.current(), [])

  // The panels stop the test when they close; this covers a device lost while one is open (P2.55).
  useEffect(() => {
    if (micUnavailable) stopMicTestRef.current()
  }, [micUnavailable])

  return { testMicrophone, cancelMicTest, micTestState }
}

export default useMicrophoneTest
